/**
 * Acronym-aware search (ADR-0009 §4), same rules as the approved mockup:
 *  - doc number fragments ("SEC-003", "ITA-POL")
 *  - title / description words; terms of 3 characters or fewer must match the
 *    START of a word (so "IR" does not hit "first" or "requirements")
 *  - acronyms from search_terms ("IR" -> "Incident Response")
 *  - automatic title initials ("IR", "IRP" -> "Incident Response Policy")
 *  - framework tags ("CMMC", "SOC 2") - ADR-0012
 * v1 later moves this into PostgreSQL full-text search with the same expansion.
 */
export interface Searchable {
  title: string;
  description: string;
  base_number: string;
  full_number: string;
  frameworks?: string[];
}
export interface Term {
  term: string;
  expansion: string;
}
export interface Hit<T> {
  item: T;
  score: number;
  why: string[];
}

const STOP = new Set(["and", "&", "of", "the", "for", "to", "a", "an", "in", "on"]);

export function initialsOf(title: string): string {
  return title
    .split(/[\s\-/]+/)
    .filter((w) => w && !STOP.has(w.toLowerCase()))
    .map((w) => w[0]!)
    .join("")
    .toUpperCase();
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function inText(text: string, term: string): boolean {
  const t = term.toLowerCase();
  if (t.length <= 3) return new RegExp(`(^|[^a-z0-9])${escapeRe(t)}`, "i").test(text);
  return text.toLowerCase().includes(t);
}

export function search<T extends Searchable>(q: string, items: T[], terms: Term[]): { hits: Hit<T>[]; expansions: Term[] } {
  const query = q.trim();
  if (!query) return { hits: items.map((item) => ({ item, score: 0, why: [] })), expansions: [] };
  const words = query.split(/\s+/);
  const expansions: Term[] = [];
  const expanded = words.map((w) => {
    const t = terms.find((x) => x.term.toLowerCase() === w.toLowerCase());
    if (t && !expansions.some((e) => e.term.toLowerCase() === t.term.toLowerCase())) expansions.push(t);
    return t ? t.expansion.toLowerCase() : null;
  });

  const hits: Hit<T>[] = [];
  for (const item of items) {
    const title = item.title.toLowerCase();
    const desc = item.description.toLowerCase();
    const nums = `${item.full_number} ${item.base_number}`.toLowerCase();
    const ini = initialsOf(item.title);
    const tags = (item.frameworks ?? []).join(" | ").toLowerCase();
    let score = 0;
    const why: string[] = [];
    const tagHit = (item.frameworks ?? []).find((f) => f.toLowerCase() === query.toLowerCase());
    if (tagHit) {
      score += 60;
      why.push(`tag ${tagHit}`);
    }
    if (nums.includes(query.toLowerCase())) {
      score += 100;
      why.push("doc number");
    }
    words.forEach((w, i) => {
      let hit = false;
      if (inText(title, w)) {
        score += 40;
        hit = true;
        if (!why.includes("title")) why.push("title");
      }
      const exp = expanded[i];
      if (exp && (title.includes(exp) || desc.includes(exp))) {
        score += 35;
        hit = true;
        why.push(`${w.toUpperCase()} = ${exp.replace(/\b\w/g, (c) => c.toUpperCase())}`);
      }
      if (w.length >= 2 && /^[a-z]+$/i.test(w) && ini.startsWith(w.toUpperCase())) {
        score += 30;
        hit = true;
        why.push(`initials ${ini}`);
      }
      if (!hit && !tagHit && tags && inText(tags, w)) {
        score += 25;
        hit = true;
        if (!why.some((x) => x.startsWith("tag "))) why.push("tag");
      }
      if (!hit && inText(desc, w)) {
        score += 10;
        if (!why.includes("description")) why.push("description");
      }
    });
    if (score > 0) hits.push({ item, score, why: [...new Set(why)] });
  }
  hits.sort((a, b) => b.score - a.score);
  return { hits, expansions };
}
